import type { OfferGetters } from './types';

export function useOfferGetters(): OfferGetters {
  // Getters for OfferRes properties
  const getOffersListFromResponse = (offers: ProductPaginatedOffersResponse): OfferRes[] =>
    offers.content ?? [];

  const getProductVariantFromOffer = (offer: OfferRes): ProductVariant => {
    return offer?.offerInventory?.variant ?? null;
  };

  const getOfferInventory = (offer: OfferRes): OfferProductInventory | null => {
    return offer?.offerInventory ?? null;
  };

  const getEstimatedDeliveryDates = (offer: OfferRes): OfferDeliveryDates[] | [] => {
    return offer?.estimatedDeliveryDates ?? [];
  };

  const getSupplier = (offer: OfferRes): OfferSupplier => {
    return offer?.supplier ?? null;
  };

  // Getters for OfferPriceRes properties
  const getPriceRanges = (offerPrice: OfferPriceRes): OfferPriceRange[] => {
    return offerPrice?.priceRanges ?? [];
  };

  const getProductPrice = (priceRange: OfferPriceRange): OfferProductPrice => {
    return priceRange?.price ?? null;
  };

  const getDiscountPrice = (priceRange: OfferPriceRange): OfferProductPrice => {
    return priceRange?.discountPrice ?? null;
  };

  // Getters for OfferProductInventory properties
  const getInventoryId = (inventory: OfferProductInventory): string => inventory.id;
  const getQuantityPerItem = (inventory: OfferProductInventory): number =>
    inventory.quantityPerItem;
  const getStock = (inventory: OfferProductInventory): number => inventory.stock;
  const getBrand = (inventory: OfferProductInventory): string => inventory.brand;
  const getExternalSource = (inventory: OfferProductInventory): string => inventory.externalSource;
  const getStatus = (inventory: OfferProductInventory): string => inventory.status;
  const getCurrency = (inventory: OfferProductInventory): string => inventory.currency;
  const getPackingType = (inventory: OfferProductInventory): string => inventory.packingType;
  const getProductUnit = (inventory: OfferProductInventory): string => inventory.productUnit;
  const getMaxOrderQuantity = (inventory: OfferProductInventory): number =>
    inventory.maxOrderQuantity;
  const getMinOrderQuantity = (inventory: OfferProductInventory): number =>
    inventory.minOrderQuantity;
  const getMinStockAlert = (inventory: OfferProductInventory): number => inventory.minStockAlert;
  const getMinShippingPrice = (inventory: OfferProductInventory): number =>
    inventory.minShippingPrice;
  const getMinShippingPriceAdditional = (inventory: OfferProductInventory): number =>
    inventory.minShippingPriceAdditional;
  const getMinShippingType = (inventory: OfferProductInventory): number =>
    inventory.minShippingType;
  const getMinShippingZone = (inventory: OfferProductInventory): string =>
    inventory.minShippingZone as unknown as string;
  const getLeadTimeToShip = (inventory: OfferProductInventory): number => inventory.leadTimeToShip;
  const getCustomFieldValues = (inventory: OfferProductInventory): OfferCustomFieldValueDto[] =>
    inventory.customFieldValues;

  // Get the best offer (the one with the lowest price)
  const getBestOfferFromList = (offers: OfferRes[]): OfferRes | null => {
    if (!offers || offers.length === 0) return null;

    // Filter offers with stock > 0 and at least one offer price
    const filteredOffers = offers.filter(
      (offer) => offer.offerInventory.stock > 0 && offer.offerPrices.length > 0
    );

    // If no offers match the filter, use the original offers list
    const validOffers = filteredOffers.length > 0 ? filteredOffers : offers;

    // Find the offer with the lowest item price
    const bestOffer = validOffers.reduce((prev, current) => {
      const prevPrice =
        getBestOfferPriceFromOffer(prev)?.priceRanges[0].price.itemPrice ?? Infinity;
      const currentPrice =
        getBestOfferPriceFromOffer(current)?.priceRanges[0].price.itemPrice ?? Infinity;
      return currentPrice < prevPrice ? current : prev;
    });
    return bestOffer;
  };

  // Get the unit price (HT) of an offer
  const getUnitPriceHT = (offer: OfferRes): number | null => {
    if (!offer.offerPrices.length) return null;

    let unitPriceHT: number | null = null;

    offer.offerPrices.forEach((offerPrice) => {
      offerPrice.priceRanges.forEach((priceRange) => {
        if (unitPriceHT === null || priceRange.price.unitPrice < unitPriceHT) {
          unitPriceHT = priceRange.price.unitPrice;
        }
      });
    });

    return unitPriceHT;
  };

  // Get the unit price (TTC) of an offer
  const getUnitPriceTTC = (offer: OfferRes): number | null => {
    if (!offer.offerPrices.length) return null;

    let unitPriceTTC: number | null = null;

    offer.offerPrices.forEach((offerPrice) => {
      offerPrice.priceRanges.forEach((priceRange) => {
        if (unitPriceTTC === null || priceRange.price.unitPriceTTC < unitPriceTTC) {
          unitPriceTTC = priceRange.price.unitPriceTTC;
        }
      });
    });

    return unitPriceTTC;
  };

  // Get the lowest unit price (HT) from a single OfferPrice (Related Product)
  const getUnitPriceHTFromBestOffer = (bestOffer: ProductBestOffer): number | null => {
    if (!bestOffer?.offerPrice.priceRanges.length) return null;

    return bestOffer?.offerPrice.priceRanges.reduce(
      (lowest, priceRange) => {
        return lowest === null || priceRange.price.unitPrice < lowest
          ? priceRange.price.unitPrice
          : lowest;
      },
      null as number | null
    );
  };

  // Get the lowest unit price (TTC) from a single OfferPrice (Related Product)
  const getUnitPriceTTCFromBestOffer = (bestOffer: ProductBestOffer): number | null => {
    if (!bestOffer.offerPrice.priceRanges.length) return null;

    return bestOffer.offerPrice.priceRanges.reduce(
      (lowest, priceRange) => {
        return lowest === null || priceRange.price.unitPriceTTC < lowest
          ? priceRange.price.unitPriceTTC
          : lowest;
      },
      null as number | null
    );
  };

  // Get offer prices based on type
  const getOfferPrices = (offer: OfferRes): OfferPriceRes[] => {
    if (offer?.offerPrices?.length > 0) {
      const offersACCOUNT = offer.offerPrices.filter(
        (offerPrice) => offerPrice.offerPriceType === 'ACCOUNT'
      );
      const offersGROUP = offer.offerPrices.filter(
        (offerPrice) => offerPrice.offerPriceType === 'GROUP'
      );
      const offersPUBLIC = offer.offerPrices.filter(
        (offerPrice) => offerPrice.offerPriceType === 'PUBLIC'
      );
      return offersACCOUNT.length > 0
        ? offersACCOUNT
        : offersGROUP.length > 0
          ? offersGROUP
          : offersPUBLIC;
    } else {
      return [];
    }
  };

  // Get the best offer price from an offer
  const getBestOfferPriceFromOffer = (offerInventory: OfferRes): OfferPriceRes | null => {
    if (!offerInventory.offerPrices.length || offerInventory.offerInventory.stock <= 0) {
      return null;
    }

    const offers = getOfferPrices(offerInventory);
    if (offers.length === 0) {
      return null;
    }

    return offers.reduce((prev, current) =>
      prev.priceRanges[0].price.itemPrice < current.priceRanges[0].price.itemPrice ? prev : current
    );
  };

  // Getter to get a custom field value by its external ID
  const getCustomFieldValueByExternalId = (offer: OfferRes, externalId: string): string | null => {
    return (
      offer.offerInventory.customFieldValues.find(
        (field) => field.customField.externalId === externalId
      )?.value ?? null
    );
  };

  const getOfferPriceId = (offerPrice: OfferPriceRes): string => offerPrice.id;

  const getOfferPriceExternalId = (offerPrice: OfferExternalId): string => offerPrice.externalId;

  const getOfferPriceType = (offerPrice: OfferPriceRes): string => offerPrice.offerPriceType;

  const getOfferPriceItemPerPack = (offerPrice: OfferPriceRes): number => offerPrice.itemPerPack;

  const getOfferPriceCustomerAccountId = (offerPrice: OfferPriceRes): string =>
    offerPrice.customerAccountId;

  const getOfferPriceCustomerTagId = (offerPrice: OfferPriceRes): string =>
    offerPrice.customerTagId;

  // Price Ranges
  const getOfferPriceRanges = (offerPrice: OfferPriceRes): OfferPriceRange[] =>
    offerPrice.priceRanges;

  const getOfferPriceRangeByQuantity = (
    offerPrice: OfferPriceRes,
    quantity: number
  ): OfferPriceRange | undefined =>
    offerPrice.priceRanges.find((range: any) => range.quantity === quantity);

  // Price details from the first price range
  const getOfferPriceItemPrice = (offerPrice: OfferPriceRes): number =>
    offerPrice.priceRanges?.[0]?.price?.itemPrice;

  const getOfferPriceItemPriceTTC = (offerPrice: OfferPriceRes): number =>
    offerPrice.priceRanges?.[0]?.price?.itemPriceTTC;

  const getOfferPriceUnitPrice = (offerPrice: OfferPriceRes): number =>
    offerPrice.priceRanges?.[0]?.price?.unitPrice;

  const getOfferPriceUnitPriceTTC = (offerPrice: OfferPriceRes): number =>
    offerPrice.priceRanges?.[0]?.price?.unitPriceTTC;

  const getOfferPriceDiscountPrice = (offerPrice: OfferPriceRes): OfferProductPrice =>
    offerPrice.priceRanges?.[0]?.discountPrice;

  return {
    getOffersListFromResponse,
    getProductVariantFromOffer,
    getOfferInventory,
    getEstimatedDeliveryDates,
    getSupplier,
    getPriceRanges,
    getProductPrice,
    getDiscountPrice,
    getInventoryId,
    getQuantityPerItem,
    getStock,
    getBrand,
    getExternalSource,
    getStatus,
    getCurrency,
    getPackingType,
    getProductUnit,
    getMaxOrderQuantity,
    getMinOrderQuantity,
    getMinStockAlert,
    getMinShippingPrice,
    getMinShippingPriceAdditional,
    getMinShippingType,
    getMinShippingZone,
    getLeadTimeToShip,
    getCustomFieldValues,
    getUnitPriceHT,
    getUnitPriceTTC,
    getOfferPrices,
    getBestOfferPriceFromOffer,
    getBestOfferFromList,
    getCustomFieldValueByExternalId,
    getUnitPriceHTFromBestOffer,
    getUnitPriceTTCFromBestOffer,
    getOfferPriceId,
    getOfferPriceExternalId,
    getOfferPriceType,
    getOfferPriceItemPerPack,
    getOfferPriceCustomerAccountId,
    getOfferPriceCustomerTagId,
    getOfferPriceRanges,
    getOfferPriceRangeByQuantity,
    getOfferPriceItemPrice,
    getOfferPriceItemPriceTTC,
    getOfferPriceUnitPrice,
    getOfferPriceUnitPriceTTC,
    getOfferPriceDiscountPrice,
  };
}
